// apps/web/src/features/admin/form-config/shared/formula-lang.ts
//
// CodeMirror 6 language extension for formula expressions.
// Provides syntax highlighting (via decorations) and autocomplete.

import {
  ViewPlugin,
  Decoration,
  type DecorationSet,
  type EditorView,
  type ViewUpdate,
} from '@codemirror/view';
import {
  type CompletionContext,
  type CompletionResult,
  type Completion,
  autocompletion,
} from '@codemirror/autocomplete';
import type { Extension } from '@codemirror/state';
import {
  FORMULA_FUNCTION_CATALOG,
  type FormulaFunctionCategory,
} from '@capture/rules-engine';

// ---------------------------------------------------------------------------
// Function registry — DERIVED from the engine catalog (P4 T4)
//
// FORMULA_FUNCTION_CATALOG (packages/rules-engine) is THE single source of
// truth. The autocomplete groups, the highlighter's function set, and the
// tokenizer regex all derive from it here — so a function added to the engine
// automatically appears in the editor, and the two can never drift. The parity
// suite (`formula-lang-catalog-parity.test.ts`) pins the equality.
// ---------------------------------------------------------------------------

/** Engine category → editor display group. Order here is the display order. */
const CATEGORY_DISPLAY: Array<{ category: FormulaFunctionCategory; label: string }> = [
  { category: 'math', label: 'Math' },
  { category: 'logic', label: 'Logic' },
  { category: 'string', label: 'String' },
  { category: 'date', label: 'Date' },
  { category: 'lines', label: 'Line Items' },
  { category: 'batch', label: 'Batch Aggregation' },
  { category: 'quality', label: 'Quality' },
  { category: 'txn', label: 'Transaction' },
];

/** Categories the autocomplete filters out of EVERY editor scope. `txn`
 *  (TXN_* remittance aggregates) is meaningful only inside a transaction-scoped
 *  formula; this editor's scope model is `record | batch` and has no txn scope
 *  yet, so TXN_* is hidden here — but it still lives in ALL_FUNCTIONS (so it
 *  highlights + tokenizes) and in the catalog, i.e. a documented filter, not an
 *  omission. Follow-up: a `txn` editor scope reveals the Transaction group. */
export const HIDDEN_AUTOCOMPLETE_CATEGORIES: ReadonlySet<FormulaFunctionCategory> = new Set([
  'txn',
]);

export const ALL_FUNCTIONS: readonly string[] = FORMULA_FUNCTION_CATALOG.map((f) => f.name);

export type FormulaFunction = string;

export const FUNCTION_CATEGORIES: Record<string, readonly string[]> = Object.fromEntries(
  CATEGORY_DISPLAY.map(({ category, label }) => [
    label,
    FORMULA_FUNCTION_CATALOG.filter((f) => f.category === category).map((f) => f.name),
  ]).filter(([, names]) => (names as string[]).length > 0),
);

/** Autocomplete `detail` per function — its catalog signature + description. */
const FUNCTION_DETAIL: Record<string, string> = Object.fromEntries(
  FORMULA_FUNCTION_CATALOG.map((f) => [f.name, `${f.signature} — ${f.description}`]),
);

/** Signature + optional-filter documentation for the line-item aggregates.
 *  Retained (P2a) for the callers that surface it directly; the values come
 *  from the catalog's line-item signatures so they can't drift. */
export const LINE_FUNCTION_SIGNATURES: Record<string, string> = Object.fromEntries(
  FORMULA_FUNCTION_CATALOG.filter((f) => f.category === 'lines').map((f) => [
    f.name,
    `${f.signature} — ${f.description}`,
  ]),
);

/** The function names the autocomplete offers for a given editor scope.
 *  `record` hides batch aggregates (they need cross-document context); every
 *  scope hides {@link HIDDEN_AUTOCOMPLETE_CATEGORIES}. Exported for the parity
 *  test so the visible set is asserted against the catalog, not snapshotted. */
export function visibleFunctionNames(scope?: 'record' | 'batch'): string[] {
  return FORMULA_FUNCTION_CATALOG.filter((f) => {
    if (HIDDEN_AUTOCOMPLETE_CATEGORIES.has(f.category)) return false;
    if (scope === 'record' && f.category === 'batch') return false;
    return true;
  }).map((f) => f.name);
}

// ---------------------------------------------------------------------------
// Tokenizer — regex-based token stream
// ---------------------------------------------------------------------------

interface Token {
  from: number;
  to: number;
  type: 'fieldRef' | 'function' | 'operator' | 'number' | 'string' | 'error';
}

const TOKEN_PATTERNS: Array<{ re: RegExp; type: Token['type'] }> = [
  { re: /\{[A-Za-z_]\w*\}/, type: 'fieldRef' },
  { re: /\{[^}]*\}/, type: 'error' }, // malformed field ref
  { re: /\{/, type: 'error' }, // unclosed brace
  // function names must be followed by ( to be recognized as functions
  {
    re: new RegExp(
      `(?:${ALL_FUNCTIONS.join('|')})(?=\\s*\\()`,
    ),
    type: 'function',
  },
  { re: /[+\-*/%]|[=!<>]=?|&&|\|\|/, type: 'operator' },
  { re: /\d+(?:\.\d+)?/, type: 'number' },
  { re: /"(?:[^"\\]|\\.)*"/, type: 'string' },
  { re: /'(?:[^'\\]|\\.)*'/, type: 'string' },
];

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  const combined = new RegExp(
    TOKEN_PATTERNS.map((p) => `(${p.re.source})`).join('|'),
    'g',
  );

  let match: RegExpExecArray | null;
  while ((match = combined.exec(text)) !== null) {
    // Find which group matched
    for (let i = 0; i < TOKEN_PATTERNS.length; i++) {
      if (match[i + 1] !== undefined) {
        tokens.push({
          from: match.index,
          to: match.index + match[0].length,
          type: TOKEN_PATTERNS[i]!.type,
        });
        break;
      }
    }
  }
  return tokens;
}

// ---------------------------------------------------------------------------
// Decoration classes
// ---------------------------------------------------------------------------

const decorationForType: Record<Token['type'], Decoration> = {
  fieldRef: Decoration.mark({ class: 'cm-formula-fieldRef' }),
  function: Decoration.mark({ class: 'cm-formula-function' }),
  operator: Decoration.mark({ class: 'cm-formula-operator' }),
  number: Decoration.mark({ class: 'cm-formula-number' }),
  string: Decoration.mark({ class: 'cm-formula-string' }),
  error: Decoration.mark({ class: 'cm-formula-error' }),
};

function buildDecorations(view: EditorView): DecorationSet {
  const text = view.state.doc.toString();
  const tokens = tokenize(text);
  const decorations = tokens.map((t) =>
    decorationForType[t.type].range(t.from, t.to),
  );
  return Decoration.set(decorations, true);
}

// ---------------------------------------------------------------------------
// ViewPlugin — formulaHighlighter
// ---------------------------------------------------------------------------

export const formulaHighlighter = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = buildDecorations(view);
    }
    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildDecorations(update.view);
      }
    }
  },
  { decorations: (v) => v.decorations },
);

// ---------------------------------------------------------------------------
// Autocomplete
// ---------------------------------------------------------------------------

function makeFieldCompletions(fieldNames: string[]): Completion[] {
  return fieldNames.map((name) => ({
    label: `{${name}}`,
    type: 'variable' as const,
    apply: `{${name}}`,
  }));
}

function makeFunctionCompletions(
  scope?: 'record' | 'batch',
): Completion[] {
  return visibleFunctionNames(scope).map((name) => ({
    label: name,
    type: 'function' as const,
    apply: `${name}()`,
    // Every function surfaces its catalog signature + description as `detail`.
    ...(FUNCTION_DETAIL[name] ? { detail: FUNCTION_DETAIL[name] } : {}),
  }));
}

export function formulaAutocomplete(
  fieldNames: string[],
  scope?: 'record' | 'batch',
): Extension {
  const fieldCompletions = makeFieldCompletions(fieldNames);
  const functionCompletions = makeFunctionCompletions(scope);

  function completionSource(
    context: CompletionContext,
  ): CompletionResult | null {
    // Field completion: triggered by {
    const fieldMatch = context.matchBefore(/\{[A-Za-z_]\w*/);
    if (fieldMatch) {
      return {
        from: fieldMatch.from,
        options: fieldCompletions,
      };
    }

    // Function completion: triggered by uppercase letters
    const funcMatch = context.matchBefore(/[A-Z_]+/);
    if (funcMatch && funcMatch.text.length >= 1) {
      return {
        from: funcMatch.from,
        options: functionCompletions,
        filter: true,
      };
    }

    return null;
  }

  return autocompletion({
    override: [completionSource],
  });
}

// ---------------------------------------------------------------------------
// CSS
// ---------------------------------------------------------------------------

export const FORMULA_EDITOR_CSS = `
.cm-formula-fieldRef {
  color: #c27040;
  font-weight: 600;
}
.cm-formula-function {
  color: #2563eb;
  font-weight: 600;
}
.cm-formula-operator {
  color: #6b7280;
}
.cm-formula-number {
  color: #059669;
}
.cm-formula-string {
  color: #d97706;
}
.cm-formula-error {
  text-decoration: wavy underline red;
  text-decoration-skip-ink: none;
}
`;
